//
// Copyright 2024 DXOS.org
//

import { useAtomValue } from '@effect-atom/atom-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { type Registry, type View } from '@dxos/echo';
import { Popover } from '@dxos/react-ui';
import { FieldEditor } from '@dxos/react-ui-form';

import { type ModalController, type TableModel } from '../../model';

type ColumnSettingsProps = {
  registry?: Registry.Registry;
  model?: TableModel;
  modals: ModalController;
  onNewColumn: () => void;
};

export const ColumnSettings = ({ registry, model, modals, onNewColumn }: ColumnSettingsProps) => {
  const [newField, setNewField] = useState<View.FieldType>();
  const state = useAtomValue(modals.state);

  useEffect(() => {
    if (state?.type === 'columnSettings' && state.mode.type === 'create' && model?.projection) {
      setNewField(model.projection.createFieldProjection());
      requestAnimationFrame(() => {
        onNewColumn();
      });
    } else {
      setNewField(undefined);
    }
  }, [model?.projection, state]);

  const existingField = useMemo(() => {
    if (state?.type === 'columnSettings') {
      const { mode } = state;
      if (mode.type === 'edit') {
        return model?.projection?.getFields().find((f) => f.id === mode.fieldId);
      }
    }
    return undefined;
  }, [model?.projection, state]);

  const field = existingField ?? newField;

  const handleSave = useCallback(() => {
    modals.close();
  }, [modals]);

  const handleCancel = useCallback(() => {
    if (state?.type === 'columnSettings' && state.mode.type === 'create' && newField) {
      model?.projection.deleteFieldProjection(newField.id);
    }
  }, [model?.projection, state, newField]);

  if (!model?.projection || !field) {
    return null;
  }

  return (
    <Popover.Root modal={false} open={state?.type === 'columnSettings'}>
      <Popover.VirtualTrigger virtualRef={modals.trigger} />
      <Popover.Portal>
        <Popover.Content classNames='md:w-64'>
          <Popover.Viewport>
            <FieldEditor
              projection={model.projection}
              field={field}
              registry={registry}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </Popover.Viewport>
          <Popover.Arrow />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
